import {
  AnalysisResult,
  AnalysisSet,
  CheckResult,
  ComponentDesign,
  Lumber,
  Member,
  MemberStressSummary,
} from '@paragon/contract-library';
import { lumberCanBeUsedForComponentThickness } from '@paragon/material-lists';

import type { RootState } from '../../store';
import { loadActiveAnalysisResults } from '../../store/actions/loadAnalysisResults';
import { analyzeComponentDesign } from '../../store/actions/paragonProcessActions';
import { getActiveAnalysisSet } from '../../store/selectors/getActiveAnalysisSetSelector';
import { getAnalysisResultGuids } from '../../store/selectors/getAnalysisResultsForActiveAnalysisSetSelector';
import { getLoadCasesForActiveComponentDesign } from '../../store/selectors/getLoadCasesForActiveComponentDesignSelector';
import { getUniqueLumbersInLumberListForActiveComponentDesign } from '../../store/selectors/getUniqueLumbersInLumberList';
import { setDetailedAnalysis } from '../../store/slices/applicationSlice';
import { addChatEntry } from '../assistantStore';
import {
  describeLumber,
  describeMemberGeometry,
  findMemberByName,
  getTrussMembers,
  openTruss,
  requireString,
  roundPoint,
} from './helpers';
import {
  DesignValueLookup,
  getGoverningDesignValues,
  loadDesignValues,
  rankStrongerLumber,
  readDesignValues,
} from './lumberStrength';
import { defineTool, ToolError } from './types';

// Checks over this ratio fail; one at exactly 1.00 passes, as the truss's own result does. Only failing checks are
// reported: a check that passes isn't something to fix, however close it is.
const FAILING_RATIO = 1;
const MAX_PROBLEMS = 8;
const MAX_CHECKS_PER_PROBLEM = 3;
const RESULTS_TIMEOUT_MS = 30_000;

interface CheckWithLoadCase {
  readonly check: CheckResult;
  readonly loadCase: string | null;
}

const round = (value: number) => Math.round(value);

/** A link's forces from the analysis, in pounds and inch-pounds, for the panel a member check names. */
function describeLinkForces(summary: MemberStressSummary | undefined) {
  if (summary == null) return null;
  const value = (entry: { value: number | string } | null) => (entry == null ? null : round(Number(entry.value)));
  return {
    csi: Number(summary.csi.value),
    compressionLb: value(summary.compression),
    tensionLb: value(summary.tension),
    midPanelMomentInLb: value(summary.adjustedMidPanelMoment),
    endMomentsInLb: [value(summary.adjustedIEndMoment), value(summary.adjustedJEndMoment)],
    shearLb: value(summary.shear),
  };
}

const MAX_LUMBER_OPTIONS = 6;

/**
 * What the member could be instead, judged by the design values its failing checks depend on, so the Assistant never
 * has to guess which grade is stronger: the current values, and the lumber that fits and is stronger, best first. When
 * nothing is stronger, it says so, since only a change to the truss will help then.
 */
function describeLumberOptions(
  member: Member,
  checkNames: readonly string[],
  fits: readonly Lumber[],
  designValues: DesignValueLookup | null,
) {
  const governing = [...new Set(checkNames.flatMap(getGoverningDesignValues))];
  const currentSet = designValues?.(member.lumber) ?? null;
  const ranked = designValues == null ? null : rankStrongerLumber(member.lumber, fits, governing, designValues);

  if (governing.length === 0 || currentSet == null || ranked == null)
    return {
      lumberThatFits: [...new Set(fits.map((lumber) => describeLumber({ ...member, lumber })))].filter(
        (option) => option !== describeLumber(member),
      ),
      lumberNote:
        governing.length === 0
          ? 'A different grade doesn’t change these checks.'
          : 'Design values couldn’t be loaded, so these aren’t ranked by strength.',
    };

  return {
    designValuesPsi: readDesignValues(currentSet, governing),
    strongerLumber: ranked
      .slice(0, MAX_LUMBER_OPTIONS)
      .map(({ lumber, values }) => ({ lumber: describeLumber({ ...member, lumber }), ...values })),
    ...(ranked.length === 0
      ? {
          lumberNote:
            `No lumber that fits is stronger in ${governing.join(', ')}, so a different grade won’t fix this. ` +
            'Change its webbing instead: stack the chord or add a web to shorten the panel.',
        }
      : {}),
  };
}

/**
 * Groups failing check results by the member or plate they're about, worst first,
 * with where each is and what governs it, in the truss's terms.
 */
export function describeProblems(
  componentDesign: ComponentDesign,
  analysisSet: AnalysisSet,
  checks: readonly CheckWithLoadCase[],
  lumberList: readonly Lumber[] = [],
  designValues: DesignValueLookup | null = null,
) {
  const members = getTrussMembers(componentDesign);
  const memberNames = new Set(members.map((member) => member.name));
  const { analogModel } = analysisSet;
  const stressByLink = new Map(analysisSet.memberStressSummaries.map((summary) => [summary.linkGuid, summary]));

  const groups = new Map<string, CheckWithLoadCase[]>();
  for (const entry of checks) {
    if (entry.check.capacity <= FAILING_RATIO) continue;
    const plate = entry.check.objects.find((object) => object.type === 'Plate Pair');
    const member = entry.check.objects.find((object) => memberNames.has(object.name));
    const key = plate != null ? `plate ${plate.name}` : member != null ? `member ${member.name}` : null;
    if (key == null) continue;
    groups.set(key, [...(groups.get(key) ?? []), entry]);
  }

  return [...groups.entries()]
    .map(([key, entries]) => {
      // The worst result of each check, so the same check under several load cases reads once.
      const worst = new Map<string, CheckWithLoadCase>();
      for (const entry of entries)
        if ((worst.get(entry.check.name)?.check.capacity ?? -1) < entry.check.capacity)
          worst.set(entry.check.name, entry);
      const governing = [...worst.values()]
        .sort((a, b) => b.check.capacity - a.check.capacity)
        .slice(0, MAX_CHECKS_PER_PROBLEM);
      const ratio = governing[0].check.capacity;

      const checksSummary = governing.map(({ check, loadCase }) => {
        const link = check.objects.find((object) => object.type === 'Link');
        const analogLink =
          link == null ? null : analogModel.analogLinks.find((candidate) => candidate.guid === link.guid);
        return {
          check: check.name,
          category: check.checkCategory,
          capacityRatio: check.capacity,
          loadCase,
          tpiSection: check.tpiSection,
          ...(analogLink != null
            ? {
                panel: {
                  from: roundPoint(analogLink.iJoint),
                  to: roundPoint(analogLink.jJoint),
                  forces: describeLinkForces(stressByLink.get(analogLink.guid)),
                },
              }
            : {}),
        };
      });

      if (key.startsWith('plate ')) {
        const jointName = key.slice('plate '.length);
        const joint = analogModel.analogJoints.find((candidate) => candidate.name === jointName);
        const meeting = [
          ...new Set(entries.flatMap(({ check }) => check.objects.filter((object) => memberNames.has(object.name)))),
        ].map((object) => object.name);
        return {
          problem: 'plate',
          joint: `Joint ${jointName}`,
          ...(joint != null ? { at: roundPoint(joint) } : {}),
          members: [...new Set(meeting)],
          capacityRatio: ratio,
          checks: checksSummary,
        };
      }

      const name = key.slice('member '.length);
      const member = members.find((candidate) => candidate.name === name)!;
      return {
        problem: 'member',
        member: name,
        type: member.memberType,
        lumber: describeLumber(member),
        ...describeLumberOptions(
          member,
          governing.map(({ check }) => check.name),
          lumberList.filter((lumber) => lumberCanBeUsedForComponentThickness(lumber, member.thickness)),
          designValues,
        ),
        centerline: describeMemberGeometry(member, componentDesign.component).centerline,
        capacityRatio: ratio,
        checks: checksSummary,
      };
    })
    .sort((a, b) => b.capacityRatio - a.capacityRatio)
    .slice(0, MAX_PROBLEMS);
}

/** Waits for the analysis's results for every load case, which the app fetches after a detailed analysis. */
async function waitForResults(getState: () => RootState, guids: readonly string[]) {
  const deadline = Date.now() + RESULTS_TIMEOUT_MS;
  for (;;) {
    const requests = guids.map((guid) => getState().analysisResultRequests[guid]);
    if (requests.every((request) => request?.status === 'loaded' || request?.status === 'error'))
      return requests.flatMap((request) => (request?.status === 'loaded' ? [request.item as AnalysisResult] : []));
    if (Date.now() > deadline) throw new ToolError('The detailed analysis results took too long to load.');
    await new Promise((resolve) => setTimeout(resolve, 200));
  }
}

export const diagnosisTools = [
  defineTool<{ truss: string }>({
    name: 'diagnose_truss',
    description:
      'Runs a detailed analysis of a truss as it is, without upgrading it, and says what fails and why: each failing ' +
      'member or plate, with the checks that govern it, their load cases ' +
      'and TPI sections, where it is, and the forces in the panel. Use it whenever the designer wants to know why a ' +
      'truss fails or how to fix it, before suggesting fixes.',
    inputSchema: { type: 'object', properties: { truss: { type: 'string' } }, required: ['truss'] },
    changesProject: false,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const wasDetailed = getState().application.detailedAnalysis;
      const analyzedBefore = getState().componentDesignTimestamps[componentDesign.guid]?.lastAnalyzed;

      // A detailed analysis keeps every check for every load case; the designer's own setting is left as it was.
      dispatch(setDetailedAnalysis(true));
      try {
        await dispatch(
          analyzeComponentDesign(componentDesign, {
            selectWhenFinished: 'ifComponentDesignIsActive',
            addSpinner: true,
          }),
        );
      } catch {
        // DesignServer can fail to analyze a truss whose loads changed since its last upgrade; upgrading rebuilds them.
        throw new ToolError(
          `The detailed analysis of ${componentDesign.component.name} failed. Run upgrade_and_analyze on it, then ` +
            'diagnose it again.',
        );
      } finally {
        dispatch(setDetailedAnalysis(wasDetailed));
      }
      if (getState().componentDesignTimestamps[componentDesign.guid]?.lastAnalyzed === analyzedBefore)
        throw new ToolError(`${componentDesign.component.name} couldn’t be analyzed.`);

      const analysisSet = getActiveAnalysisSet(getState());
      if (analysisSet == null || analysisSet.componentDesign.guid !== componentDesign.guid)
        throw new ToolError(`${componentDesign.component.name}’s analysis didn’t load.`);
      const guids = getAnalysisResultGuids(analysisSet) ?? [];
      await dispatch(loadActiveAnalysisResults());
      const results = await waitForResults(getState, guids);

      const loadCaseNames = new Map(
        getLoadCasesForActiveComponentDesign(getState()).map((loadCase) => [loadCase.guid, loadCase.name]),
      );
      const checks = results.flatMap((result) =>
        result.checkResults.map((check) => ({ check, loadCase: loadCaseNames.get(result.loadCaseGuid) ?? null })),
      );
      // Load the design values of each failing member's lumber and of what could replace it, to rank them.
      const lumberList = getUniqueLumbersInLumberListForActiveComponentDesign(getState());
      const failingMembers = describeProblems(componentDesign, analysisSet, checks).flatMap((problem) =>
        'member' in problem ? [findMemberByName(componentDesign, problem.member)] : [],
      );
      const designValues = await loadDesignValues(dispatch, getState, [
        ...failingMembers.map((member) => member.lumber),
        ...failingMembers.flatMap((member) =>
          lumberList.filter((lumber) => lumberCanBeUsedForComponentThickness(lumber, member.thickness)),
        ),
      ]);
      const problems = describeProblems(componentDesign, analysisSet, checks, lumberList, designValues);

      return {
        summary: {
          truss: componentDesign.component.name,
          capacityRatio: analysisSet.capacity,
          passes: analysisSet.capacity <= FAILING_RATIO && analysisSet.errors.length === 0,
          problems,
          errors: analysisSet.errors,
          warnings: analysisSet.warnings,
        },
      };
    },
  }),

  defineTool<{ problem: string; fixes: { label: string; request: string }[] }>({
    name: 'offer_fixes',
    description:
      'Offers the designer fixes for a problem you explained, each with a Try it button. A fix they pick comes back ' +
      'as their next message, and you then plan it as usual. Give two or three fixes that would each work on their ' +
      'own, such as upsizing a member or adding a web, best first. `request` is what the designer would say to ask ' +
      'for it, specific enough to plan from: the truss, members, sizes, and positions in inches.',
    inputSchema: {
      type: 'object',
      properties: {
        problem: { type: 'string', description: 'The problem in one line, such as "W7 fails in combined bending".' },
        fixes: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              label: { type: 'string', description: 'Short, such as "Upsize W7 to 2x6".' },
              request: {
                type: 'string',
                description: 'Such as "On FT-1, change W7 to 2x6 and run Upgrade and Analyze."',
              },
            },
            required: ['label', 'request'],
          },
        },
      },
      required: ['problem', 'fixes'],
    },
    changesProject: false,
    async execute(input, { chatId }) {
      const problem = requireString(input, 'problem');
      const fixes = (Array.isArray(input.fixes) ? input.fixes : [])
        .filter((fix) => typeof fix?.label === 'string' && typeof fix.request === 'string')
        .map(({ label, request }) => ({ label: label.trim(), request: request.trim() }))
        .filter(({ label, request }) => label !== '' && request !== '');
      if (fixes.length === 0) throw new ToolError('Give at least one fix with a label and a request.');

      // Agents have no chat to show buttons in; they get the fixes back to present themselves.
      if (chatId != null) addChatEntry(chatId, { type: 'fixes', problem, fixes });
      return { summary: { offered: fixes.map(({ label }) => label) } };
    },
  }),
];
